import React, {
    useContext,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';

import { analyticService } from 'global/services';

import { LocaleContext } from 'app/containers/LocaleProvider';
import DishDAC from 'app/views/Menu/Menu/DAC/DishDAC';
import { ConfigContext } from '../ConfigProvider';
import { BasketContext } from '../BasketProvider';

import { DishLayoutDefault } from './DishLayoutDefault';
import { DishLayoutWithToggle } from './DishLayoutWithToggle';
import { DishLayoutPrimary } from './DishLayoutPrimary';
import { DishLayoutSecondary } from './DishLayoutSecondary';
import { DishLayoutUpsell } from './DishLayoutUpsell';
import { DishAgeGate } from './DishAgeGate';
import { DishLayoutThin } from './DishLayoutThin';
import { DishLayoutWideButtonCard } from './DishLayoutWideButtonCard';

function Dish({
    layout,
    index,
    category,
    id,
    parentId,
    isSoldOut = false,
    isWeightTimeHidden,
    isTimeHidden,
    hideWeightOnDesktop,
    title: titleRu, // array
    titleEn,
    titleForSmallCards,
    description: descriptionRu = '',
    descriptionEn,
    partnersComment,
    portion,
    portions: dishPortions,
    maxPortion = 9,
    originalPriceWithStep,
    discountPriceWithStep,
    price,
    originalPrice,
    dishCountStep,
    imageUrl = '',
    imageUrlDesktop = '',
    verticalImage = '',
    time = '',
    timeText: timeTextRu = '',
    timeTextEn,
    weightValue,
    weight = '',
    weightText: weightTextRu = '',
    weightTextEn,
    isCentered = false,
    buttonsDisabled = false,
    canChangePortionsCount = false,
    areDetailsEnabled = false,
    buttonText,
    buttonTextAction,
    linkUrl,
    imageLinkUrl,
    typeformWidgetId,
    isNovelty,
    colors,
    promoCheckoutDishObserver,
    nutrition,
    isNutritionEnabled,
    isLastDish,
    metaLabel,
    slides,
    hour = undefined,
    minutes = undefined,
    hourText = '',
    minutesText = '',
    triggerAgeGate,
    giftWrapId = undefined,
    tags,
    dishesById,
    basketDishesById,
    isDishRemovingDisabled,
    categoryId,
}) {
    const { locale } = useContext(LocaleContext);
    const {
        joinDishTitlesinMobile,
        pageId,
        v2Layout,
    } = useContext(ConfigContext);
    const isHarryPage = pageId === 'hp';
    const isRomanticPage = pageId === 'romantic';

    const currentLayout = useMemo(() => {
        if (isHarryPage) {
            return 'with_toggle';
        }

        if (isRomanticPage) {
            return 'wideButtonCard';
        }

        return layout;
    }, [isHarryPage, isRomanticPage, layout]);

    const { basket } = useContext(BasketContext);

    const titles = {
        ru: titleRu,
        en: titleEn,
    };
    const title = titles[locale];

    const descriptions = {
        ru: descriptionRu,
        en: descriptionEn,
    };
    const description = descriptions[locale];

    const timeTexts = {
        ru: timeTextRu,
        en: timeTextEn,
    };
    const timeText = timeTexts[locale];
    const weightTexts = {
        ru: weightTextRu,
        en: weightTextEn,
    };
    const weightText = weightTexts[locale];

    const containerRef = useRef(null);

    useEffect(() => {
        promoCheckoutDishObserver.observe(containerRef.current);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    const joinedTitle = title.join(' ');

    const trackClickOnDish = useCallback(() => {
        analyticService.push({
            eventName: 'Track_Card_Click',
            id,
            title: joinedTitle,
            caption: '',
            price,
            categoryId: category,
            index,
            subscriptionType: 'singlePay',
            list: pageId,
        });
    }, []);

    const trackAddToBasket = useCallback(({ portions }) => {
        analyticService.push({
            eventName: 'add_to_cart',
            dishes: [{
                id,
                title: joinedTitle,
                caption: '',
                price,
                portion: portions,
                categoryId: category,
            }],
            source: pageId,
            subscriptionType: 'singlePay',
            typeOfSet: basket.typeOfSet,
        });
    }, []);

    return (
        /* eslint-disable jsx-a11y/no-static-element-interactions */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        <div onClick={trackClickOnDish} ref={containerRef} data-id={id}>
            { /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
            /* eslint-enable jsx-a11y/click-events-have-key-events */}

            {currentLayout === 'wideButtonCard' && (
                <DishLayoutWideButtonCard
                    index={index}
                    id={id}
                    categoryId={categoryId}
                    tags={tags}
                    isSoldOut={isSoldOut}
                    isDishRemovingDisabled={isDishRemovingDisabled}
                    isWeightTimeHidden={isWeightTimeHidden}
                    partnersComment={partnersComment}
                    portion={portion}
                    dishPortions={dishPortions}
                    maxPortion={maxPortion}
                    originalPriceWithStep={originalPriceWithStep}
                    discountPriceWithStep={discountPriceWithStep}
                    price={price}
                    dishCountStep={dishCountStep}
                    imageUrl={imageUrl}
                    imageUrlDesktop={imageUrlDesktop}
                    time={time}
                    weightValue={weightValue}
                    weight={weight}
                    isCentered={isCentered}
                    buttonsDisabled={buttonsDisabled}
                    canChangePortionsCount={canChangePortionsCount}
                    areDetailsEnabled={areDetailsEnabled}
                    buttonText={buttonText}
                    buttonTextAction={buttonTextAction}
                    typeformWidgetId={typeformWidgetId}
                    linkUrl={linkUrl}
                    imageLinkUrl={imageLinkUrl}
                    isNovelty={isNovelty}
                    colors={colors}
                    nutrition={nutrition}
                    isNutritionEnabled={isNutritionEnabled}
                    joinDishTitlesinMobile={joinDishTitlesinMobile}
                    title={title}
                    description={description}
                    timeText={timeText}
                    weightText={weightText}
                    trackAddToBasket={trackAddToBasket}
                    isLastDish={isLastDish}
                    metaLabel={metaLabel}
                    slides={slides}
                    v2Layout={v2Layout}
                    giftWrapId={giftWrapId}
                    pageId={pageId}
                    hour={hour}
                    minutes={minutes}
                    hourText={hourText}
                    minutesText={minutesText}
                />
            )}

            {currentLayout === 'primary' && (
                <DishLayoutPrimary
                    id={id}
                    price={price}
                    portion={portion}
                    dishPortions={dishPortions}
                    dishCountStep={dishCountStep}
                    discountPriceWithStep={discountPriceWithStep}
                    originalPrice={originalPrice}
                    trackAddToBasket={trackAddToBasket}
                />
            )}
            {currentLayout === 'secondary' && (
                <DishLayoutSecondary
                    colors={colors}
                    id={id}
                    portion={portion}
                    dishPortions={dishPortions}
                    price={price}
                    originalPrice={originalPrice}
                    discountPriceWithStep={discountPriceWithStep}
                    dishCountStep={dishCountStep}
                    buttonsDisabled={buttonsDisabled}
                    index={index}
                    imageUrl={imageUrl}
                    imageUrlDesktop={imageUrlDesktop}
                    joinDishTitlesinMobile={joinDishTitlesinMobile}
                    title={title}
                    description={description}
                    partnersComment={partnersComment}
                    trackAddToBasket={trackAddToBasket}
                />
            )}
            {currentLayout === 'upsell' && (
                <DishLayoutUpsell
                    id={id}
                    portion={portion}
                    dishPortions={dishPortions}
                    price={price}
                    dishCountStep={dishCountStep}
                    imageUrl={imageUrl}
                    imageUrlDesktop={imageUrlDesktop}
                    title={title}
                    trackAddToBasket={trackAddToBasket}
                />
            )}
            {currentLayout === 'default' && (
                <DishLayoutDefault
                    index={index}
                    id={id}
                    parentId={parentId}
                    dishesById={dishesById}
                    basketDishesById={basketDishesById}
                    isDishRemovingDisabled={isDishRemovingDisabled}
                    tags={tags}
                    isSoldOut={isSoldOut}
                    isWeightTimeHidden={isWeightTimeHidden}
                    isTimeHidden={isTimeHidden}
                    partnersComment={partnersComment}
                    portion={portion}
                    dishPortions={dishPortions}
                    maxPortion={maxPortion}
                    originalPriceWithStep={originalPriceWithStep}
                    discountPriceWithStep={discountPriceWithStep}
                    price={price}
                    dishCountStep={dishCountStep}
                    imageUrl={imageUrl}
                    imageUrlDesktop={imageUrlDesktop}
                    time={time}
                    weightValue={weightValue}
                    weight={weight}
                    isCentered={isCentered}
                    buttonsDisabled={buttonsDisabled}
                    canChangePortionsCount={canChangePortionsCount}
                    areDetailsEnabled={areDetailsEnabled}
                    buttonText={buttonText}
                    buttonTextAction={buttonTextAction}
                    typeformWidgetId={typeformWidgetId}
                    linkUrl={linkUrl}
                    imageLinkUrl={imageLinkUrl}
                    isNovelty={isNovelty}
                    colors={colors}
                    nutrition={nutrition}
                    isNutritionEnabled={isNutritionEnabled}
                    joinDishTitlesinMobile={joinDishTitlesinMobile}
                    title={title}
                    description={description}
                    timeText={timeText}
                    weightText={weightText}
                    trackAddToBasket={trackAddToBasket}
                    isLastDish={isLastDish}
                    metaLabel={metaLabel}
                    slides={slides}
                    v2Layout={v2Layout}
                    giftWrapId={giftWrapId}
                    pageId={pageId}
                    hour={hour}
                    minutes={minutes}
                    hourText={hourText}
                    minutesText={minutesText}
                />
            )}
            {currentLayout === 'with_toggle' && (
                <DishLayoutWithToggle
                    index={index}
                    id={id}
                    tags={tags}
                    isSoldOut={isSoldOut}
                    isWeightTimeHidden={isWeightTimeHidden}
                    partnersComment={partnersComment}
                    portion={portion}
                    dishPortions={dishPortions}
                    maxPortion={maxPortion}
                    originalPriceWithStep={originalPriceWithStep}
                    discountPriceWithStep={discountPriceWithStep}
                    price={price}
                    dishCountStep={dishCountStep}
                    imageUrl={imageUrl}
                    imageUrlDesktop={imageUrlDesktop}
                    time={time}
                    weightValue={weightValue}
                    weight={weight}
                    isCentered={isCentered}
                    buttonsDisabled={buttonsDisabled}
                    canChangePortionsCount={canChangePortionsCount}
                    areDetailsEnabled={areDetailsEnabled}
                    buttonText={buttonText}
                    buttonTextAction={buttonTextAction}
                    typeformWidgetId={typeformWidgetId}
                    linkUrl={linkUrl}
                    imageLinkUrl={imageLinkUrl}
                    isNovelty={isNovelty}
                    colors={colors}
                    nutrition={nutrition}
                    isNutritionEnabled={isNutritionEnabled}
                    joinDishTitlesinMobile={joinDishTitlesinMobile}
                    title={title}
                    description={description}
                    timeText={timeText}
                    weightText={weightText}
                    trackAddToBasket={trackAddToBasket}
                    isLastDish={isLastDish}
                    metaLabel={metaLabel}
                    slides={slides}
                    v2Layout={v2Layout}
                    giftWrapId={giftWrapId}
                    pageId={pageId}
                    hour={hour}
                    minutes={minutes}
                    hourText={hourText}
                    minutesText={minutesText}
                />
            )}
            {currentLayout === 'newCards' && (
                <DishLayoutThin
                    index={index}
                    id={id}
                    tags={tags}
                    isSoldOut={isSoldOut}
                    isWeightTimeHidden={isWeightTimeHidden}
                    hideWeightOnDesktop={hideWeightOnDesktop}
                    partnersComment={partnersComment}
                    portion={portion}
                    dishPortions={dishPortions}
                    maxPortion={maxPortion}
                    originalPriceWithStep={originalPriceWithStep}
                    discountPriceWithStep={discountPriceWithStep}
                    price={price}
                    dishCountStep={dishCountStep}
                    imageUrl={imageUrl}
                    imageUrlDesktop={imageUrlDesktop}
                    verticalImage={verticalImage}
                    time={time}
                    weightValue={weightValue}
                    weight={weight}
                    isCentered={isCentered}
                    buttonsDisabled={buttonsDisabled}
                    canChangePortionsCount={canChangePortionsCount}
                    areDetailsEnabled={areDetailsEnabled}
                    buttonText={buttonText}
                    buttonTextAction={buttonTextAction}
                    typeformWidgetId={typeformWidgetId}
                    linkUrl={linkUrl}
                    imageLinkUrl={imageLinkUrl}
                    isNovelty={isNovelty}
                    colors={colors}
                    nutrition={nutrition}
                    isNutritionEnabled={isNutritionEnabled}
                    joinDishTitlesinMobile={joinDishTitlesinMobile}
                    title={title}
                    titleForSmallCards={titleForSmallCards}
                    description={description}
                    timeText={timeText}
                    weightText={weightText}
                    trackAddToBasket={trackAddToBasket}
                    isLastDish={isLastDish}
                    metaLabel={metaLabel}
                    slides={slides}
                    v2Layout={v2Layout}
                    giftWrapId={giftWrapId}
                    pageId={pageId}
                    hour={hour}
                    minutes={minutes}
                    hourText={hourText}
                    minutesText={minutesText}
                />
            )}
            <DishAgeGate triggerAgeGate={triggerAgeGate} containerRef={containerRef} />
        </div>
    );
}

Dish.propTypes = {
    index: PropTypes.number.isRequired,
    category: PropTypes.string.isRequired,
    isSoldOut: PropTypes.bool,
    hideWeightOnDesktop: PropTypes.bool,
    id: PropTypes.string.isRequired,
    title: PropTypes.arrayOf(PropTypes.string).isRequired,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    portion: PropTypes.number.isRequired,
    maxPortion: PropTypes.number,
    originalPriceWithStep: PropTypes.string.isRequired,
    discountPriceWithStep: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    originalPrice: PropTypes.number.isRequired,
    dishCountStep: PropTypes.number.isRequired,
    imageUrl: PropTypes.string,
    imageUrlDesktop: PropTypes.string,
    time: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    timeText: PropTypes.string,
    weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    weightText: PropTypes.string,
    triggerAgeGate: PropTypes.bool.isRequired,
    isCentered: PropTypes.bool,
    buttonsDisabled: PropTypes.bool,
    canChangePortionsCount: PropTypes.bool,
    areDetailsEnabled: PropTypes.bool,
    promoCheckoutDishObserver: PropTypes.shape({ observe: PropTypes.func }).isRequired,

    isLastDish: PropTypes.bool.isRequired,
    giftWrapId: PropTypes.string,
    dishesById: PropTypes.objectOf(PropTypes.shape({})),
    basketDishesById: PropTypes.objectOf(PropTypes.shape({})),
};


export default React.memo(Dish);
